import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import MaskedTextField from '@/common/components/MaskedTextField'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { fiscalDocumentKindOptions } from '@/common/constants/fiscalDocumentKindOptions.constant'
import { numericRegexMask } from '@/common/constants/masks.constant'
import { options as unitForQuantityOptions } from '@/common/constants/unitForQuantityOptions'
import { useEquipmentCategories } from '@/common/hooks/queries/useEquipmentCategories'
import { useEquipmentTiers } from '@/common/hooks/queries/useEquipmentTiers'
import { useProductCategories } from '@/common/hooks/queries/useProductCategories'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { EquipmentTierData } from '@/common/types/equipmentTier'
import { FormTransformers } from '@/common/utils/formTransformers'
import { EquipmentCategoryData } from '@/features/equipmentModels/types/equipmentCategories'
import { tefProviderOptions, tefProviders } from '@/features/establishments/constants/tefModels.constant'
import { ProductCategoryData } from '@/features/productCategories/types/productCategories'
import { useCreateProduct } from '@/features/products/hooks/useCreateProduct'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight, PlusIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { ProductValidationSchema, createSchema } from '../../schemas/create.schema'

const Create = () => {
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchProductCategory, setSearchProductCategory] = useDebounce('')
  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')
  const [searchEquipmentTier, setSearchEquipmentTier] = useDebounce('')

  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors },
  } = useForm<ProductValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      product_tef_identifiers: [{ identifier: '' }],
    },
  })

  const {
    fields: productTefIdentifierFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'product_tef_identifiers',
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreateProductPermission = !isUserPermissionsPending ?  hasUserPermission('Admin::Products::Create') : true

  const watchProductTefIdentifiers = watch('product_tef_identifiers')

  const { mutate: createProduct, isPending: createProductIsPending } = useCreateProduct({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      goBack({ fallback: '/products' })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategory },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: equipmentCategoriesOptions } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  })

  const { data: equipmentTierOptions } = useEquipmentTiers({
    filters: { search: searchEquipmentTier },
    mapper: Mapper.mapToOptions<EquipmentTierData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  })

  const onSubmit: SubmitHandler<ProductValidationSchema> = (data) => createProduct(data)

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasCreateProductPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/products" />

            <p className="text-white-400 font-normal">Produtos</p>

            <ChevronsRight className="text-white-400" />

            <h2>Novo produto</h2>
          </div>

          <div className="flex gap-4">
            <Link to="/products">
              <Button variant="outline-error">Cancelar</Button>
            </Link>

            <Button
              data-testid="create_products"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={createProductIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>

        <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto">
          <p className="text-lg font-bold">Novo produto</p>

          <SelectController
            control={control}
            name="category"
            markAsRequired
            dataTestid="category_selector"
            label="Categoria"
            onInputChange={setSearchProductCategory}
            options={productCategoryOptions || []}
            errorMessage={errors?.category?.message || undefined}
            isDisabled={createProductIsPending}
          />

          <div className="flex gap-2">
            <TextField
              label="Nome"
              markAsRequired
              data-testid="display_name_input"
              errorMessage={errors?.display_name?.message || undefined}
              className="w-1/2"
              {...register('display_name')}
              disabled={createProductIsPending}
            />

            <div className="w-1/2">
              <SelectController
                control={control}
                name="unit_for_quantity"
                label="Métrica"
                markAsRequired
                data-testid="unit_for_quantity_selector"
                options={unitForQuantityOptions}
                errorMessage={errors?.unit_for_quantity?.message || undefined}
                isDisabled={createProductIsPending}
              />
            </div>
          </div>

          <div className="flex w-full flex-col gap-4">
            <p className="text-xs -mb-3">Identificadores</p>

            {productTefIdentifierFields.map((field, index) => {
              const tefProvider = watchProductTefIdentifiers?.[index]?.tef_provider?.value

              return (
                <div key={field.id} className="flex gap-2 items-start">
                  <SelectController
                    control={control}
                    name={`product_tef_identifiers.${index}.tef_provider`}
                    label="Provedor TEF"
                    dataTestid={`product_tef_identifiers_${index}_tef_provider_selector`}
                    options={tefProviderOptions}
                    className="w-full"
                    isClearable
                    errorMessage={errors.product_tef_identifiers?.[index]?.tef_provider?.message || undefined}
                    isDisabled={createProductIsPending}
                  />

                  {!!tefProvider ? (
                    <Controller
                      control={control}
                      name={`product_tef_identifiers.${index}.identifier`}
                      render={({ field }) => (
                        <MaskedTextField
                          {...field}
                          dataTestid={`product_tef_identifiers_${index}_identifier_input`}
                          label={`Identificador ${tefProvider ? tefProviders[tefProvider]?.label : ''}`}
                          mask={
                            tefProvider
                              ? tefProviders[tefProvider].regexMaskFn(tefProviders[tefProvider].codeLength)
                              : numericRegexMask(6)
                          }
                          prepareChar={(char) => char.toUpperCase()}
                          className="w-full"
                          errorMessage={errors.product_tef_identifiers?.[index]?.identifier?.message || undefined}
                          disabled={createProductIsPending}
                        />
                      )}
                    />
                  ) : (
                    <TextField label="Identificador" placeholder="Preencha o Provedor" className="w-full" disabled />
                  )}

                  {index === 0 ? (
                    <Button
                      type="button"
                      variant="outline-error"
                      onClick={() => append({ tef_provider: { value: null, label: '' }, identifier: '' })}
                      disabled={createProductIsPending}
                      className="size-[3.25rem] flex items-center justify-center mt-5"
                    >
                      <PlusIcon className="text-blue-800" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline-error"
                      onClick={() => remove(index)}
                      disabled={createProductIsPending}
                      className="size-[3.25rem] flex items-center justify-center mt-5"
                    >
                      <Trash2Icon className="text-danger" />
                    </Button>
                  )}
                </div>
              )
            })}
          </div>

          {!!watchProductTefIdentifiers?.length &&
          watchProductTefIdentifiers?.some((productTefIdentifier) => !!productTefIdentifier?.identifier) ? (
            <SelectController
              control={control}
              name="equipment_category_ids"
              dataTestid="equipment_category_ids_selector"
              label="Categorias de equipamento"
              onInputChange={setSearchEquipmentCategory}
              options={equipmentCategoriesOptions || []}
              isMulti
              errorMessage={errors?.equipment_category_ids?.message || undefined}
              isDisabled={createProductIsPending}
            />
          ) : (
            <TextField
              label="Categorias de equipamento"
              placeholder="Preencha ao menos um Identificador para selecionar"
              disabled
            />
          )}

          <SelectController
            control={control}
            name="equipment_tier"
            label="Porte"
            dataTestid="equipment_tier_selector"
            options={equipmentTierOptions || []}
            onInputChange={setSearchEquipmentTier}
            isClearable
            errorMessage={errors?.equipment_tier?.message}
            isDisabled={createProductIsPending}
          />

          <SelectController
            control={control}
            name="fiscal_document_kind"
            dataTestid="fiscal_document_kind_selector"
            label="Tipo de Documento Fiscal"
            errorMessage={errors?.fiscal_document_kind?.message}
            options={fiscalDocumentKindOptions}
            isDisabled={createProductIsPending}
          />
        </Box>

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
